import React, { useState } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  GET_EMPLOYEE_FEEDBACK_ANALYTICS,
  GET_ORDER_FEEDBACK_BY_ORDER,
} from "./graphql";
import Loader from "../../../Loader";
import { AiOutlineStar, AiFillStar, AiOutlineInfoCircle } from "react-icons/ai";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

const EmployeeFeedbackDashboard = ({
  employeeId,
  allEmployeeIds,
  startDate,
  endDate,
  isOverall,
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  const {
    data: analyticsData,
    loading: analyticsLoading,
    error: analyticsError,
  } = useQuery(GET_EMPLOYEE_FEEDBACK_ANALYTICS, {
    variables: {
      employeeIds: allEmployeeIds || (employeeId ? [employeeId] : []),
      startDate,
      endDate,
    },
    fetchPolicy: "network-only",
  });

  const [
    fetchOrderFeedback,
    { data: orderFeedbackData, loading: orderFeedbackLoading },
  ] = useLazyQuery(GET_ORDER_FEEDBACK_BY_ORDER);

  const handleRatingClick = (orderIds) => {
    if (orderIds && orderIds.length > 0) {
      setSelectedOrderIds(orderIds);
      fetchOrderFeedback({ variables: { orderIds } });
    }
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  if (analyticsLoading) return <Loader />;
  if (analyticsError)
    return <div className="text-red-500 p-4">Error loading analytics</div>;

  const analytics = analyticsData?.getEmployeeFeedbackAnalytics?.data;
  if (!analytics)
    return <div className="text-gray-400 p-4">No feedback data available</div>;

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {isOverall && (
        <div className="flex items-center gap-3 mb-2">
          <div className="w-2 h-8 bg-[#7F58E6] rounded-full"></div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
            Employee Feedback Insights
          </h2>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-xl hover:border-[#7F58E6]/30 transition-all duration-300 group">
          <p className="text-gray-500 text-sm font-medium mb-2 uppercase tracking-widest">
            Average Rating
          </p>
          <div className="flex items-center gap-3">
            <span className="text-5xl font-black text-gray-900 group-hover:text-[#7F58E6] transition-colors">
              {analytics.averageRating?.toFixed(1) || "0.0"}
            </span>
            <div className="flex flex-col items-start">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star}>
                    {star <= Math.round(analytics.averageRating || 0) ? (
                      <AiFillStar className="text-[#7F58E6] text-xl" />
                    ) : (
                      <AiOutlineStar className="text-gray-300 text-xl" />
                    )}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 font-medium mt-1">
                out of 5.0
              </p>
            </div>
          </div>
          <div className="mt-6 px-4 py-1.5 bg-[#7F58E6]/10 rounded-full border border-[#7F58E6]/20">
            <p className="text-[#7F58E6] text-xs font-bold uppercase tracking-tighter">
              {analytics.totalFeedbacks} Total Reviews
            </p>
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-gray-200 rounded-3xl p-8 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-500 text-sm font-medium uppercase tracking-widest">
              Rating Distribution
            </p>
            <AiOutlineInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
          </div>
          <div className="space-y-4">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = analytics.ratingDistribution[rating]?.count || 0;
              const orderIds =
                analytics.ratingDistribution[rating]?.orderIds || [];
              const percentage =
                analytics.totalFeedbacks > 0
                  ? (count / analytics.totalFeedbacks) * 100
                  : 0;

              return (
                <div
                  key={rating}
                  className="flex items-center gap-4 group cursor-pointer"
                  onClick={() => handleRatingClick(orderIds)}
                >
                  <span className="text-sm font-bold text-gray-500 w-4 group-hover:text-[#7F58E6] transition-colors">
                    {rating}
                  </span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                    <div
                      className="h-full bg-gradient-to-r from-[#7F58E6] to-[#9B77F3] shadow-[0_0_10px_rgba(127,88,230,0.3)] group-hover:brightness-110 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center gap-2 w-16 justify-end">
                    <span className="text-sm font-bold text-gray-900">
                      {count}
                    </span>
                    <span className="text-[10px] text-gray-500 font-bold">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Questions Analytics */}
      {analytics.questionsAnalytics &&
        analytics.questionsAnalytics.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-6 bg-[#6366F1] rounded-full"></div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                Performance metrics
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {analytics.questionsAnalytics.map((q) => (
                <div
                  key={q.questionId}
                  className="bg-white border border-gray-200 rounded-3xl p-6 shadow-lg hover:shadow-[#6366F1]/10 transition-all duration-300"
                >
                  <p className="text-sm font-bold text-gray-700 mb-6 min-h-[40px] leading-relaxed">
                    {q.question}
                  </p>
                  <div className="space-y-5">
                    {Object.entries(q.selectedOptionsCount).map(
                      ([option, data]) => {
                        const percentage =
                          analytics.totalFeedbacks > 0
                            ? (data.count / analytics.totalFeedbacks) * 100
                            : 0;
                        return (
                          <div
                            key={option}
                            className="space-y-2 cursor-pointer group"
                            onClick={() => handleRatingClick(data.orderIds)}
                          >
                            <div className="flex justify-between items-end">
                              <span className="text-xs font-semibold text-gray-600 group-hover:text-gray-900 transition-colors">
                                {option}
                              </span>
                              <span className="text-[10px] font-black text-[#6366F1] bg-[#6366F1]/10 px-2 py-0.5 rounded-md">
                                {data.count}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] group-hover:brightness-125 transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Orders Overlay / List */}
      {selectedOrderIds && (
        <div className="fixed inset-0 z-[100] bg-gray-900/60 backdrop-blur-md flex justify-center items-center p-4 sm:p-8 animate-in zoom-in-95 duration-300">
          <div className="bg-gray-50 border border-gray-200 rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-5 sm:p-8 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#7F58E6] rounded-2xl flex items-center justify-center text-white shrink-0">
                  <AiOutlineStar className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight">
                    Feedback Details
                  </h3>
                  <p className="text-sm font-bold text-[#7F58E6]">
                    {selectedOrderIds.length} Verified Feedbacks
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrderIds(null)}
                className="self-start sm:self-auto bg-gray-100 text-gray-700 hover:bg-gray-200 px-6 py-3 rounded-2xl font-bold transition-all border border-gray-200"
              >
                Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar bg-gray-50">
              {orderFeedbackLoading ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-4">
                  <Loader />
                  <p className="text-gray-500 font-bold animate-pulse">
                    Loading detailed feedbacks...
                  </p>
                </div>
              ) : (
                orderFeedbackData?.getOrderFeedbackByOrder?.data?.map((fb) => (
                  <div
                    key={fb.id}
                    className="bg-white border border-gray-200 rounded-[2rem] overflow-hidden hover:border-gray-300 transition-colors shadow-sm"
                  >
                    <div
                      className="p-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 cursor-pointer group"
                      onClick={() => toggleOrderExpand(fb.id)}
                    >
                      <div className="flex items-center gap-5 min-w-0">
                        <div className="bg-gray-50 w-18 h-18 px-3 py-2 rounded-2xl flex flex-col items-center justify-center border border-gray-100 group-hover:bg-[#7F58E6]/10 group-hover:border-[#7F58E6]/20 transition-all text-center shrink-0">
                          <span className="text-[10px] font-black text-gray-400 group-hover:text-[#7F58E6]">
                            BOOKING
                          </span>
                          <span className="text-sm font-black text-gray-800 group-hover:text-[#7F58E6]">
                            #
                            {fb.order?.bookingId ||
                              (fb.orderId
                                ? String(fb.orderId).slice(-4)
                                : "N/A")}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                              Customer Experience
                            </p>
                            {fb.order?.bookingDate && (
                              <>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <p className="text-xs font-bold text-[#7F58E6]">
                                  {new Date(
                                    fb.order.bookingDate,
                                  ).toLocaleDateString(undefined, {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                  , {fb.order.timeSlot}
                                </p>
                              </>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1.5 bg-[#7F58E6]/10 px-3 py-1 rounded-full border border-[#7F58E6]/20 shrink-0">
                              <span className="text-[#7F58E6] font-black text-lg">
                                {fb.adminToEmployeeRating}
                              </span>
                              <AiFillStar className="text-[#7F58E6]" />
                            </div>
                            <p className="text-sm font-bold text-gray-600 break-words">
                              {fb.adminToEmployeeComment
                                ? fb.adminToEmployeeComment.substring(0, 40) +
                                  (fb.adminToEmployeeComment.length > 40
                                    ? "..."
                                    : "")
                                : "No summary available"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`self-end sm:self-auto w-10 h-10 rounded-full flex items-center justify-center transition-all ${expandedOrders[fb.id] ? "bg-[#7F58E6] text-white" : "bg-gray-100 text-gray-500"}`}
                      >
                        {expandedOrders[fb.id] ? (
                          <IoIosArrowUp className="text-xl" />
                        ) : (
                          <IoIosArrowDown className="text-xl" />
                        )}
                      </div>
                    </div>

                    {expandedOrders[fb.id] && (
                      <div className="px-8 pb-8 pt-2 border-t border-gray-100 bg-gray-50 space-y-8 animate-in slide-in-from-top-4 duration-500">
                        {fb.order?.address && (
                          <div className="space-y-4 pt-4">
                            <div className="flex items-center gap-2">
                              <div className="w-1 h-4 bg-[#10B981] rounded-full"></div>
                              <p className="text-xs text-gray-900 font-black uppercase tracking-widest">
                                Service Location
                              </p>
                            </div>
                            <div className="bg-white p-5 rounded-3xl border border-gray-100 relative shadow-sm">
                              <p className="text-sm text-gray-600 font-medium">
                                {fb.order.address}
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <div className="w-1 h-4 bg-[#7F58E6] rounded-full"></div>
                              <p className="text-xs text-gray-900 font-black uppercase tracking-widest">
                                Admin Evaluation
                              </p>
                            </div>
                            <div className="bg-white p-5 rounded-3xl border border-gray-100 relative shadow-sm">
                              <p className="text-sm text-gray-600 leading-relaxed italic">
                                "
                                {fb.adminToEmployeeComment ||
                                  "No internal comments provided by the admin for this order."}
                                "
                              </p>
                            </div>
                          </div>

                          {fb.userToEmployeeRating && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <div className="w-1 h-4 bg-[#6366F1] rounded-full"></div>
                                <p className="text-xs text-gray-900 font-black uppercase tracking-widest">
                                  Customer Voice
                                </p>
                              </div>
                              <div className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex items-center gap-2 mb-3">
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <AiFillStar
                                      key={s}
                                      className={
                                        s <= fb.userToEmployeeRating
                                          ? "text-[#7F58E6]"
                                          : "text-gray-300"
                                      }
                                    />
                                  ))}
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed font-medium">
                                  {fb.userToEmployeeComment ||
                                    "The customer provided a rating but no written feedback."}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-4 bg-gray-500 rounded-full"></div>
                            <p className="text-xs text-gray-900 font-black uppercase tracking-widest">
                              Questionnaire Breakdown
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {fb.answers?.map((ans) => (
                              <div
                                key={ans.id}
                                className="flex flex-col gap-2 p-5 bg-white rounded-3xl border border-gray-100 hover:border-[#6366F1]/20 transition-colors shadow-sm"
                              >
                                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-tighter">
                                  {ans.question?.question}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {ans.selectedOptions?.map((opt, i) => (
                                    <span
                                      key={i}
                                      className="bg-[#6366F1]/10 text-[#818CF8] px-3 py-1 rounded-full text-xs font-black border border-[#6366F1]/20"
                                    >
                                      {opt}
                                    </span>
                                  )) || (
                                    <span className="text-xs text-gray-400 italic">
                                      No option selected
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeFeedbackDashboard;
